import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    addTransactionFieldReviews,
    bulkAddFieldReviews,
} from "@api/transaction.api";
import { isViewOnlyRefusalError } from "@lib/viewOnlyRefusal";
import { Button } from "@design-system/Button/Button";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useUser } from "@hooks/useUser";
import { useAccountsByEntity } from "@hooks/useAccounts";
import buttonCountChipStyles from "@design-system/Button/buttonCountChip.module.scss";
import {
    buildBulkFieldReviewRequests,
    reviewActionDropsFromFilter,
    submitBulkFieldReviewRequests,
} from "../TransactionsBulkActions/buildBulkFieldReviewRequests";
import { TransactionsFilters } from "../filters/lib";
import {
    invalidateTransactionsQueries,
    updateTransactionInCache,
} from "../useTransactionsQuery";
import {
    getLedgerAccountsForReviewMarkable,
    getUnreviewedMarkableReviewFields,
} from "./transactionFieldReviewUtils";

interface ReviewSelectedButtonProps {
    reviewTransactionFields: TransactionReviewField[];
    selectedTransactions: TransactionListItem[];
    onClearSelection: () => void;
    /**
     * True when the selection spans the full filtered set across pages
     * ("Select all"), not just the loaded rows. In that mode `selectedTransactions`
     * only holds the currently-loaded page, so the per-line field count can't be
     * computed — fall back to showing the total transaction count instead.
     */
    hasSelectedAll: boolean;
    /** Total transactions matching the current filters; used when `hasSelectedAll`. */
    totalTransactions: number;
    filtersSelection?: TransactionsFilters | null;
    workspaceId?: string | null;
    fallbackEntityIds?: number[];
}

export const ReviewSelectedButton = ({
    reviewTransactionFields,
    selectedTransactions,
    onClearSelection,
    hasSelectedAll,
    totalTransactions,
    filtersSelection = null,
    workspaceId,
    fallbackEntityIds = [],
}: ReviewSelectedButtonProps) => {
    const user = useUser();
    const { toast, error: errorToast } = useToaster();
    const [isReviewing, setIsReviewing] = useState(false);
    const isMountedRef = useRef(true);

    const hasLedgerReviewFields = reviewTransactionFields.some(
        (field) =>
            field === TransactionReviewField.CASH_LEDGER ||
            field === TransactionReviewField.ACCRUAL_LEDGER,
    );
    const entityIds = useMemo(() => {
        if (hasSelectedAll || !hasLedgerReviewFields) {
            return [];
        }
        const uniqueIds = new Set(
            selectedTransactions.map((transaction) => transaction.entity.id),
        );
        return [...uniqueIds].sort((a, b) => a - b);
    }, [hasSelectedAll, hasLedgerReviewFields, selectedTransactions]);
    const { accountsById } = useAccountsByEntity(entityIds);

    // Same count the row Confirm chip uses: unreviewed *markable* fields
    // (empty / uncategorized values are not eligible and are not counted).
    // Cross-page "Select all" only has the loaded page, so fall back to the
    // total transaction count.
    const fieldsToReviewCount = useMemo(() => {
        if (hasSelectedAll) {
            return totalTransactions;
        }
        return selectedTransactions.reduce(
            (sum, transaction) =>
                sum +
                getUnreviewedMarkableReviewFields({
                    transaction,
                    reviewFields: reviewTransactionFields,
                    userId: user.id,
                    ledgerAccounts: getLedgerAccountsForReviewMarkable(
                        transaction,
                        accountsById,
                    ),
                }).length,
            0,
        );
    }, [
        hasSelectedAll,
        totalTransactions,
        selectedTransactions,
        reviewTransactionFields,
        user.id,
        accountsById,
    ]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const handleReview = useCallback(async () => {
        setIsReviewing(true);
        let reviewedCount = 0;
        let hasFailed = false;
        try {
            if (hasSelectedAll) {
                const requests = buildBulkFieldReviewRequests({
                    fields: reviewTransactionFields,
                    transactionIds: [],
                    filtersSelection,
                    workspaceId,
                    fallbackEntityIds,
                    transactionCount: totalTransactions,
                    dropsFromFilter: reviewActionDropsFromFilter(
                        filtersSelection,
                        reviewTransactionFields,
                        "add",
                    ),
                });
                if (requests.length > 0) {
                    reviewedCount = await submitBulkFieldReviewRequests(
                        requests,
                        bulkAddFieldReviews,
                    );
                    if (!isMountedRef.current) {
                        return;
                    }
                    await invalidateTransactionsQueries();
                }
            } else {
                for (const transaction of selectedTransactions) {
                    const fieldsToReview = getUnreviewedMarkableReviewFields({
                        transaction,
                        reviewFields: reviewTransactionFields,
                        userId: user.id,
                        ledgerAccounts: getLedgerAccountsForReviewMarkable(
                            transaction,
                            accountsById,
                        ),
                    });
                    if (fieldsToReview.length === 0) {
                        continue;
                    }
                    const reviews = await addTransactionFieldReviews(
                        transaction.id,
                        fieldsToReview,
                    );
                    if (!isMountedRef.current) {
                        return;
                    }
                    updateTransactionInCache(transaction.id, (t) => ({
                        ...t,
                        fieldReviews: reviews,
                    }));
                    reviewedCount += 1;
                }
            }
        } catch (error) {
            if (isViewOnlyRefusalError(error)) {
                return;
            }
            hasFailed = true;
        } finally {
            if (isMountedRef.current) {
                setIsReviewing(false);
            }
        }

        if (!isMountedRef.current) {
            return;
        }

        if (hasFailed) {
            // Keep the selection so the user can retry the remaining
            // transactions, but surface any partial success.
            errorToast(
                reviewedCount > 0
                    ? `Reviewed ${pluralizeWithCount(
                          "transaction",
                          reviewedCount,
                      )}, but some failed. Please try again.`
                    : "Failed to review transactions. Please try again.",
            );
            return;
        }

        onClearSelection();
        toast(
            reviewedCount > 0
                ? `Marked ${pluralizeWithCount(
                      "transaction",
                      reviewedCount,
                  )} as reviewed`
                : "Selected transactions are already reviewed",
        );
    }, [
        reviewTransactionFields,
        selectedTransactions,
        onClearSelection,
        hasSelectedAll,
        filtersSelection,
        workspaceId,
        fallbackEntityIds,
        totalTransactions,
        user.id,
        accountsById,
        toast,
        errorToast,
    ]);

    return (
        <Button
            variant="primary"
            size="md"
            onClick={handleReview}
            disabled={isReviewing}
            rightIcon={
                fieldsToReviewCount > 0 ? (
                    <span
                        data-slot-box=""
                        className={buttonCountChipStyles.countChip}
                    >
                        {fieldsToReviewCount}
                    </span>
                ) : undefined
            }
        >
            Review
        </Button>
    );
};
